// The Quote Assessment "Download Excel" export (bulk/BLK quotes). A PURE map
// from the deal, its items and the KG identity cards to a spreadsheet matrix the
// generic writer (utils/xlsx.js) turns into an .xlsx. No DOM, no fetch — so the
// column mapping and the totals maths are unit-tested on their own.
//
// The totals reuse the SAME per-unit maths as the on-screen chips
// (QuoteAssessment quoteTotals): SP and BP are per-unit, so a line's value is
// price × qty (tasks/lessons.md). Keeping the maths identical is why the sheet
// and the page can never disagree on the quoted value.
//
// Image URLs come from the item's OWN customer photos via orderedPhotoUrls — the
// one definition of "photos -> displayable URLs" the page already uses — so the
// export and the on-page ItemPhotos strip resolve URLs the same way.
//
// Bulk (BLK) quotes are the exception: the submission drain deliberately links NO
// photo to an item (bulk intake carries no per-photo tag, and guessing by order
// is forbidden — kg_quote_path ruling 15), so the per-item column is always empty
// for them. Those quotes still HAVE the customer's photos, at quote level (the
// deal Photos strip / GET /api/deals/:id/images). We list those once, at the end,
// under "Customer photos (whole quote)" — honest, never faked onto a line.
import { orderedPhotoUrls } from './ItemPhotos.jsx';

const PHOTO_BLOCK_LABEL = 'Customer photos (whole quote)';

export const EXPORT_HEADER = [
    '#', 'Customer declared name', 'Item (engine)', 'Brand', 'Model', 'Condition',
    'Qty', 'SP (each)', 'BP (each)', 'Line SP', 'Line BP', 'Image URLs',
];

// Round to at most 2 decimals, mirroring the page's `money` helper.
const round2 = n => Math.round(Number(n) * 100) / 100;
// Coerce to a number, but treat null/undefined/'' as absent (NOT 0): an unpriced
// item carries sp/bp === null, and Number(null) is 0 — that would print £0.00
// instead of a blank cell.
const num = v => (v === null || v === undefined || v === '' ? NaN : Number(v));
// A GBP currency cell for the writer, or '' when there is no finite price.
const moneyCell = v => (Number.isFinite(num(v)) ? { v: round2(v), money: true } : '');

export function buildQuoteExport(deal, items, cardsById = {}, images = []) {
    const list = items || [];

    const itemRows = list.map((it, i) => {
        const qty = Number(it.qty) || 0;
        const sp = num(it.sp);
        const bp = num(it.bp);
        const hasSp = Number.isFinite(sp);
        const hasBp = Number.isFinite(bp);
        const urls = orderedPhotoUrls(cardsById[it.quote_item_id]?.photos);
        return [
            i + 1,
            it.declared_name || '',
            it.display_name || it.name || '',
            it.brand || '',
            it.model || '',
            it.condition || '',
            qty,
            hasSp ? moneyCell(sp) : '',
            hasBp ? moneyCell(bp) : '',
            hasSp ? moneyCell(sp * qty) : '',
            hasBp ? moneyCell(bp * qty) : '',
            urls.join('\n'),
        ];
    });

    const totals = list.reduce((acc, it) => {
        const qty = Number(it.qty) || 0;
        const sp = num(it.sp);
        const bp = num(it.bp);
        acc.units += qty;
        if (Number.isFinite(bp)) acc.value += bp * qty;
        if (Number.isFinite(sp)) acc.saleValue += sp * qty;
        return acc;
    }, { units: 0, value: 0, saleValue: 0 });
    const marginPct = totals.saleValue > 0
        ? Math.round(((totals.saleValue - totals.value) / totals.saleValue) * 1000) / 10
        : null;

    const summary = [
        [],                                                     // spacer
        ['Total items', totals.units],
        ['Total quoted (BP)', moneyCell(totals.value)],
        ['Est. sale value (SP)', moneyCell(totals.saleValue)],
        ['Margin %', marginPct == null ? '' : marginPct],
    ];

    // Quote-level customer photos (bulk quotes and any deal-wide shots). One URL
    // per row so each is selectable; only added when the quote actually has any.
    const photoUrls = (images || []).filter(Boolean);
    const photoBlock = photoUrls.length
        ? [[], [PHOTO_BLOCK_LABEL], ...photoUrls.map(url => [url])]
        : [];

    const ref = deal?.reference_code || deal?.id || 'quote';
    return {
        filename: `quote-${ref}.xlsx`,
        sheetName: 'Quote',
        matrix: [EXPORT_HEADER, ...itemRows, ...summary, ...photoBlock],
    };
}
